import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import features from "@/config/features";
import {
  Car,
  BookOpen,
  Trophy,
  Target,
  Clock,
  ChevronRight,
  LogOut,
  User,
  CreditCard,
  Menu,
  X,
  Home,
  FileText,
  BarChart3,
  Crown,
  AlertCircle,
  Video,
  Play,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, refreshUser } = useAuth();
  const [videoStats, setVideoStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Check for successful payment
    const sessionId = searchParams.get("session_id");
    const status = searchParams.get("status");
    
    if (sessionId && status === "success") {
      pollPaymentStatus(sessionId);
    }
    
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info("Verifică statusul abonamentului în câteva minute.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Abonament activat cu succes!");
        await refreshUser();
        navigate("/dashboard", { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Error polling payment:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch video stats if feature enabled
      if (features.videoCourses) {
        const videoRes = await axios.get(`${API}/videos/stats`).catch(() => ({ data: null }));
        setVideoStats(videoRes.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Te-ai deconectat cu succes");
  };

  const allSidebarLinks = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    { name: "Cursuri Video", path: "/cursuri", icon: Video, requiresSubscription: true, feature: "videoCourses" },
    { name: "Teste DRPCIV", path: "/teste-drpciv", icon: Target, requiresSubscription: false },
    { name: "Abonamente", path: "/abonamente", icon: CreditCard },
  ];

  const sidebarLinks = allSidebarLinks.filter(link => !link.feature || features[link.feature]);

  const isSubscriptionActive = user?.subscription_status === "active" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dashboard-page">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-0 left-0 h-screen w-64 bg-slate-900 text-white z-50
          transform transition-transform duration-300 lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
              </div>
              <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
            </Link>
          </div>

          <nav className="p-4 space-y-2">
            {sidebarLinks.map((link) => {
              const isDisabled = link.requiresSubscription && !isSubscriptionActive;
              return (
                <Link
                  key={link.path}
                  to={isDisabled ? "/abonamente" : link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${window.location.pathname === link.path 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-800'}
                    ${isDisabled ? 'opacity-50' : ''}
                  `}
                  data-testid={`sidebar-${link.name.toLowerCase()}`}
                >
                  <link.icon className="w-5 h-5" />
                  <span>{link.name}</span>
                  {isDisabled && <Crown className="w-4 h-4 ml-auto text-yellow-400" />}
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-sm text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 min-h-screen">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Archivo']">
                Bună, {user?.name?.split(' ')[0]}! 👋
              </h1>
              <p className="text-slate-600 mt-1">
                Continuă-ți pregătirea pentru examen
              </p>
            </div>

            {/* Subscription Alert */}
            {!isSubscriptionActive && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-800">Abonament inactiv</p>
                        <p className="text-sm text-yellow-700">Activează un abonament pentru a accesa cursurile video</p>
                      </div>
                    </div>
                    <Link to="/abonamente">
                      <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                        Activează Acum
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Stats Cards */}
            {features.videoCourses && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Video className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{videoStats?.watched_videos || 0}</p>
                        <p className="text-sm text-slate-500">Video-uri Vizionate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{videoStats?.chapters_completed || 0}</p>
                        <p className="text-sm text-slate-500">Capitole Complete</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{videoStats?.total_chapters || 0}</p>
                        <p className="text-sm text-slate-500">Total Capitole</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{videoStats?.progress_percent || 0}%</p>
                        <p className="text-sm text-slate-500">Progres Total</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-['Archivo']">Acțiuni Rapide</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {features.videoCourses && (
                        <Link to={isSubscriptionActive ? "/cursuri" : "/abonamente"}>
                          <Button 
                            className={`w-full h-auto py-6 text-left justify-start ${
                              isSubscriptionActive 
                                ? 'bg-green-600 hover:bg-green-700' 
                                : 'bg-slate-600 hover:bg-slate-700'
                            }`}
                            data-testid="courses-btn"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                                <Video className="w-6 h-6" />
                              </div>
                              <div>
                                <p className="font-semibold text-lg">Cursuri Video</p>
                                <p className="text-sm opacity-80">
                                  {isSubscriptionActive ? 'Învață legislația' : 'Necesită abonament'}
                                </p>
                              </div>
                              {!isSubscriptionActive && <Crown className="w-5 h-5 text-yellow-400 ml-auto" />}
                            </div>
                          </Button>
                        </Link>
                      )}

                      <Link to="/teste-drpciv">
                        <Button 
                          className="w-full h-auto py-6 bg-blue-600 hover:bg-blue-700 text-left justify-start"
                          data-testid="start-test-btn"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Target className="w-6 h-6" />
                            </div>
                            <div>
                              <p className="font-semibold text-lg">Teste DRPCIV</p>
                              <p className="text-sm text-blue-100">Chestionare gratuite</p>
                            </div>
                          </div>
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Video Progress */}
                {features.videoCourses && isSubscriptionActive && videoStats && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-['Archivo']">Progres Cursuri Video</CardTitle>
                      <Link to="/cursuri" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                        Continuă <ChevronRight className="w-4 h-4" />
                      </Link>
                    </CardHeader>
                    <CardContent>
                      {videoStats.progress_percent > 0 ? (
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-600">Progres total</span>
                              <span className="font-semibold">{videoStats.progress_percent}%</span>
                            </div>
                            <Progress value={videoStats.progress_percent} className="h-3" />
                          </div>
                          <div className="flex items-center justify-between text-sm text-slate-600">
                            <span>{videoStats.watched_videos} din {videoStats.total_videos} video-uri vizionate</span>
                          </div>
                          <p className="text-sm text-slate-500">
                            {videoStats.progress_percent >= 80 
                              ? "🎉 Excelent! Aproape ai terminat toate cursurile!" 
                              : videoStats.progress_percent >= 50
                              ? "💪 Foarte bine! Ești pe drumul cel bun."
                              : "📚 Continuă să înveți pentru a-ți îmbunătăți cunoștințele."}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">Nu ai vizionat încă niciun video</p>
                          <Link to="/cursuri">
                            <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                              <Play className="w-4 h-4 mr-2" />
                              Începe primul curs
                            </Button>
                          </Link>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* No subscription - Promo */}
                {features.videoCourses && !isSubscriptionActive && (
                  <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Video className="w-7 h-7" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-xl mb-2">Cursuri Video Premium</h3>
                          <p className="text-blue-100 mb-4">
                            Învață legislația rutieră cu video-uri explicative, organizate pe capitole. 
                            Progresul tău este salvat și poți continua oricând.
                          </p>
                          <Link to="/abonamente">
                            <Button className="bg-white text-blue-600 hover:bg-blue-50">
                              <Crown className="w-4 h-4 mr-2" />
                              Vezi Abonamentele
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar - Subscription Status */}
              <div className="space-y-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-['Archivo']">Status Abonament</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isSubscriptionActive ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Crown className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold capitalize">{user?.subscription_plan || 'Activ'}</p>
                            <Badge className="bg-green-100 text-green-700">Activ</Badge>
                          </div>
                        </div>
                        {user?.subscription_end && (
                          <p className="text-sm text-slate-500">
                            Expiră: {new Date(user.subscription_end).toLocaleDateString('ro-RO')}
                          </p>
                        )}
                        <div className="pt-2 border-t">
                          <p className="text-sm text-slate-600 mb-2">Ai acces la:</p>
                          <ul className="text-sm space-y-1">
                            <li className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-4 h-4" /> Teste DRPCIV gratuite
                            </li>
                            {features.videoCourses && (
                              <li className="flex items-center gap-2 text-green-600">
                                <CheckCircle className="w-4 h-4" /> Cursuri video complete
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-semibold">Fără abonament</p>
                            <Badge variant="secondary">Inactiv</Badge>
                          </div>
                        </div>
                        <div className="pt-2 border-t">
                          <p className="text-sm text-slate-600 mb-2">Ai acces la:</p>
                          <ul className="text-sm space-y-1">
                            <li className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-4 h-4" /> Teste DRPCIV gratuite
                            </li>
                            {features.videoCourses && (
                              <li className="flex items-center gap-2 text-slate-400">
                                <X className="w-4 h-4" /> Cursuri video
                              </li>
                            )}
                          </ul>
                        </div>
                        <Link to="/abonamente">
                          <Button className="w-full bg-blue-600 hover:bg-blue-700">
                            Alege un plan
                          </Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Tips */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-['Archivo']">💡 Sfat</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">
                      Pentru a promova examenul DRPCIV, trebuie să răspunzi corect la minim 22 din 26 de întrebări. 
                      Exersează zilnic pentru cele mai bune rezultate!
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
